/*
 * @Author: yanghailong
 * @Date: 2023-08-23 09:06:26
 * @Last Modified by: yanghailong
 * @Last Modified time: 2025-03-27 11:59:21
 */


import { hashCode } from "./utils/index.js"

/**
 * @typedef {import('./graph-element').GraphElement} GraphElement 图元件
 */

/**
 * @typedef Vertex 顶点
 * @property {GraphElement} node
 * @property {import('konva').default.Circle} port
 */

export class Graph {
  constructor() {
    this.vertices = []
    this.adjList = new Map()
    this._portCache = new Map()
  }

  /**
   * 添加节点
   * @param {*} v
   */
  addVertex(v) {
    this.vertices.push(v)
    this.adjList.set(v, [])
  }

  /**
   * 删除节点v 并附带删除相关联的边
   * @param {GraphElement} v 要删除的节点
   * @param {boolean} deleteEdge 是否删除相关联的边
   */
  removeVertex(v, deleteEdge = true) {
    let index = this.vertices.indexOf(v)
    if (index > -1) {
      this.vertices.splice(index, 1)
      this.adjList.delete(v)
      // 删除边
      if (deleteEdge) {
        this.removeRelationEdge(v)
      }
    }
  }

  /**
   * 删除和节点v相关联的边
   * @param {GraphElement} v
   */
  removeRelationEdge(v) {
    this.adjList.forEach((list) => {
      let j = list.findIndex(item => item.node == v)
      if (j > -1) {
        // remove port
        // let e = list[j]
        // e.port && e.port.remove()
        list.splice(j, 1)
      }
    })
  }


  /**
   * 广度优先遍历
   * @param {GraphElement} node 起始节点
   * @param {(node: GraphElement) => void} callback 访问每个节点时的回调
   */
  bfs(node, callback) {
    if (!node) return
    const visited = new Set()
    const queue = [node]
    visited.add(node)
    while (queue.length) {
      const current = queue.shift()
      callback && callback(current)
      const neighbors = this.adjList.get(current) || []
      neighbors.forEach((item) => {
        if (!visited.has(item.node)) {
          visited.add(item.node)
          queue.push(item.node)
        }
      })
    }
  }

  /**
   * 深度优先遍历
   * @param {GraphElement} node 起始节点
   * @param {(node: GraphElement) => void} callback 访问每个节点时的回调
   */
  dfs(node, callback) {
    if (!node) return
    const visited = new Set()
    const traverse = (current) => {
      if (visited.has(current)) return
      visited.add(current)
      callback && callback(current)
      const neighbors = this.adjList.get(current) || []
      neighbors.forEach((item) => {
        if (!visited.has(item.node)) {
          traverse(item.node)
        }
      })
    }
    traverse(node)
  }



  /**
   * 添加边
   * @param {Vertex} v
   * @param {Vertex} w
   */
  addEdge(v, w) {
    let uuid = String(hashCode(`${v.node.uuid}-${w.node.uuid}`))
    // let edge = this.getEdgeById(uuid)
    this._portCache.set(uuid, v.port)
    // if (!edge) {
    this.adjList.get(v.node).push(w)
    // }
  }

  /**
   * 删除边
   * @param {*} v
   * @param {*} w
   */
  removeEdge(v, w) {
    let list = this.adjList.get(v)
    let index = list.indexOf(w)
    if (index > -1) {
      list.splice(index, 1)
    }
  }

  /**
   * 通过uuid获取边
   * @param {string} uuid
   * @returns
   */
  getEdgeById(uuid) {
    return this.edges.find(e => e.uuid == uuid)
  }

  /**
   * 通过uuid获取节点
   * @param {string} uuid
   * @returns {GraphElement}
   */
  getNodeById(uuid) {
    return this.vertices.find(v => v.uuid == uuid)
  }


  /**
   * 节点是否存在边
   * @param {*} node
   */
  hasEdge(node) {
    return this.adjList.get(node).length > 0
  }

  /**
   * 所有节点
   * @returns {GraphElement[]}
   */
  get nodes() {
    return this.vertices
  }


  /**
   * 所有边
   */
  get edges() {

    /**
     * @typedef Edge
     * @property {string} uuid
     * @property {Vertex} source
     * @property {Vertex} target
     */

    /**
     * @type Edge[]
     */
    const E = []


    this.adjList.forEach((list, v) => {
      list.forEach(w => {
        let uuid = String(hashCode(`${v.uuid}-${w.node.uuid}`))
        E.push({
          uuid: uuid,
          source: {
            node: v,
            port: this._portCache.get(uuid),
          },
          target: w,
        })
      })
    })


    return E
  }

  clear() {
    this.adjList.clear()
    this.vertices = []
    this._portCache.clear()
  }
}
